import { useModal } from "@hooks/useModal";
import { createModalHelpers } from "@stores/unified-modal.store";
import cn from "classnames";
import { FC } from "react";
import Modal from "../Modal";
import ModalContent from "../Modal/ModalContent";
import styles from "./Wallet.module.scss";
import { useConnectWallet } from "./hooks/useConnectWallet";
import WalletAddress from "@pages/Profile/components/WalletAddress";
import { AssetButton } from "@components/common/AssetButton";
import { useSendStore } from "@stores/send.store";
import { useSwap } from "@stores/swap.store";
import { useWallet } from "@stores/wallet.store";
import { WalletController } from "@api/WalletController";
import { showPromiseToast, showToast } from "@utils/showToast";
import { CHAIN, useTonConnectUI } from "@tonconnect/ui-react";

const Transfer: FC = () => {
    const { transfer, openModal, closeModal } = createModalHelpers();
    const [_, handleCloseModal] = useModal(() => closeModal("transfer"));
    useConnectWallet((isVisible) => (isVisible ? openModal("transfer") : closeModal("transfer")));

    const { recipient, amount, setRecipient, setAmount, memo, setMemo } = useSendStore();
    const currentWallet = useWallet((s) => s.currentWallet);
    const [tonConnectUI] = useTonConnectUI();

    const asset = useSwap((s) => s.sellAssetInfo);

    const handleTransfer = async () => {
        try {
            let result;
            if (!currentWallet.isMnemonics) {
                if (!tonConnectUI.connected) {
                    showToast("Reconnect wallet to execute transfer", "info");
                    closeModal("transfer");

                    setTimeout(() => {
                        tonConnectUI.openModal();
                    }, 500);
                    return;
                }
                result = await WalletController.trasferExternal({
                    walletGuid: currentWallet.guid,
                    amount: Number(amount),
                    recipient: recipient as string,
                    assetGuid: asset.asset.guid,
                    tag: memo as string,
                });

                if (result.status !== "success" || !result.data) {
                    showToast(result.message, "error");
                    return;
                }
                const externalMessages = result.data.messages;
                await showPromiseToast(
                    tonConnectUI.sendTransaction({
                        network: CHAIN.MAINNET,
                        validUntil: Date.now() + 1000 * 60 * 10,
                        messages: externalMessages.map(({ address, amount, payload }) => ({
                            address: address,
                            amount: amount,
                            payload,
                        })),
                    }),
                    "Transaction sent",
                );
            } else {
                result = await WalletController.trasferInternal({
                    walletGuid: currentWallet.guid,
                    amount: Number(amount),
                    recipient: recipient as string,
                    assetGuid: asset.asset.guid,
                });
            }
            if (result.status === "success") {
                showToast("Transfer successful", "success");
            } else {
                showToast("Error transferring", "error");
            }
        } catch (error) {
            showToast("Error transferring", "error");
            console.error("Error transferring:", error);
        }
    };

    return (
        <Modal
            isVisible={transfer}
            setIsVisible={(isVisible) => (isVisible ? openModal("transfer") : closeModal("transfer"))}
        >
            <ModalContent classNames={styles.content}>
                <div className={styles.header}>
                    <h2 className={styles.headline}>Withdraw</h2>
                    <button className={styles.close} onClick={handleCloseModal}>
                        <picture>
                            <source
                                srcSet="/images/modal/closemobile.svg"
                                width={7.5}
                                height={7.5}
                                media="(max-width: 768px)"
                            />
                            <img
                                height={12}
                                width={12}
                                src="/images/modal/close.svg"
                                style={{ opacity: 0.4 }}
                                alt="Close modal"
                            />
                        </picture>
                    </button>
                </div>
                <h1 className={styles.neckline}>Sender Wallet</h1>
                <WalletAddress showSelectModal={() => openModal("select")} small />
                <h1 className={styles.neckline}>Recipient Wallet</h1>
                <input
                    placeholder={"UQCiLIxw8zm..."}
                    className={cn(
                        "assented flex items-center justify-center gap-1 rounded-full h-10 pl-3 pr-2 text-sm text-foreground leading-1 outline-none font-[400] w-full",
                    )}
                    type="text"
                    inputMode="decimal"
                    name="recipient"
                    value={recipient as string}
                    onChange={(e) => setRecipient(e.target.value)}
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck="false"
                    data-form-type="other"
                />
                <div className="flex items-center justify-between gap-2 w-full">
                    <h1 className={styles.neckline}>Token and Quantity</h1>
                    <div className="flex justify-between text-sm font-medium">
                        <div className={cn(styles.description, "flex items-center gap-1 mt-0")}>
                            <img src="/images/wallet-search.svg" width={16} height={16} alt="" />
                            {asset.balance}
                        </div>
                    </div>
                </div>
                <div className="flex items-center justify-between gap-2 w-full relative">
                    <input
                        placeholder={"Quantity"}
                        className={cn(
                            "assented flex items-center justify-center gap-1 rounded-full h-10 pl-3 pr-2 text-sm text-foreground leading-1 outline-none font-[400] w-full",
                        )}
                        type="number"
                        inputMode="decimal"
                        value={amount as string}
                        onChange={(e) => setAmount(e.target.value)}
                        name="sellAmount"
                        autoComplete="off"
                        autoCorrect="off"
                        spellCheck="false"
                        data-form-type="other"
                    />
                    <AssetButton
                        className="absolute right-0 top-1/2 -translate-y-1/2 z-10 scale-95"
                        asset={asset.asset}
                        seletable={true}
                        onClick={() => openModal("currencies")}
                    />
                </div>

                <h1 className={styles.neckline}>MEMO</h1>
                <input
                    placeholder={"MEMO, tag"}
                    className={cn(
                        "assented flex items-center justify-center gap-1 rounded-full h-10 pl-3 pr-2 text-sm text-foreground leading-1 outline-none font-[400] w-full",
                    )}
                    type="text"
                    value={memo as string}
                    onChange={(e) => setMemo(e.target.value)}
                    inputMode="decimal"
                    name="sellAmount"
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck="false"
                    data-form-type="other"
                />

                <div className={cn(styles.description, "text-xs flex items-center  w-full")}>
                    Required field in case the recipient requires a MEMO
                </div>

                <div className={styles.buttons}>
                    <button
                        onClick={handleTransfer}
                        className={cn(
                            "assented flex items-center justify-between gap-1 rounded-full h-10 pl-3 pr-2 text-sm text-foreground leading-1 outline-none font-[400] w-full",
                            styles.sendBtn,
                        )}
                    >
                        <p>Send</p>
                        <img src="/images/profile/down.svg" alt="" />
                    </button>
                </div>
            </ModalContent>
        </Modal>
    );
};

export default Transfer;
